import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import * as clientEnv from 'libs/client-env';
import { Alert, Card, App } from 'antd';
import { useSession } from 'components/Authenticated';
import * as Announcements from 'api/announcements';
import { useTableView } from 'components/ResponsiveTable';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import LiveUpdateWebsocket from 'api/live-update-websocket';
import * as ProjectAPI from 'api/project-api';
import { handleProjectsViewExport } from 'libs/download-csv';
import TutorialModal from 'components/Tutorial/TutorialModal';
import { txt } from 'libs/i18n';
import { useBeeyTutorial } from 'screens/DashboardScreen/beeyTutorial';
import { getCurrentUiLocale } from 'libs/locales/ui';
import { serializeDate } from 'components/ResponsiveTable/table-view';
import tutorialModalStyles from 'components/Tutorial/TutorialModal/style.module.less';
import beeyLogo from 'resources/img/product-icon.png';
import beeyLogoGrey from 'resources/img/product-icon-grey.png';
import TranscribeModal from 'screens/DashboardScreen/components/TranscribeModal';
import { fetchCurrentTeamTags } from 'api/team-api';
import { renderMarkdown } from 'libs/markdown';
import { useAppSubdomain } from 'hooks/useAppSubdomain';
import DashboardPage from '../../components/DashboardPage';
import ProjectsToolbar from './ProjectsToolbar';
import FulltextSearchBar from './FulltextSearchBar';
import FulltextSearchTags from './FulltextSearchBar/FulltextSearchTags';
import LiveUpdateStatus from './LiveUpdateStatus';
import { useTrackedProjects } from './tracked-projects';
import { GLOBAL_SELECTION_LIMIT } from './GlobalSelectionOption';
import ProjectsTable from './ProjectsTable';
import styles from './style.module.less';
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
const PROJS_TABLE_ID = 'projects-table'; // Must be unique across all tables in Beey!!
const wrapWord = (word, wrapChar) => {
    if (word === '') {
        return '';
    }
    return `${wrapChar}${word}${wrapChar}`;
};
const convertToElasticFormat = (searchTerm, isStrict) => {
    const words = searchTerm.split(' ');
    if (isStrict) {
        return (searchTerm !== '' ? `"${searchTerm.replace(/"|'/g, '')}"` : '');
    }
    const isTermWithQuotes = (searchTerm.trim().startsWith('"') && searchTerm.trim().endsWith('"'));
    const wrapChar = isTermWithQuotes ? '' : '*';
    const wrapped = words.map((word) => {
        const isWordWithQuotes = word.startsWith('"') && word.endsWith('"');
        if (isWordWithQuotes) {
            return word;
        }
        return wrapWord(word, wrapChar);
    }).join(' ');
    return (searchTerm !== '' ? `^${wrapped}` : '');
};
const constructProjectsQueryFromView = (viewParams) => {
    if (viewParams.sieve === null) {
        throw TypeError('viewParams.sieve is null');
    }
    const { sortBy, sortOrder, createdDates, updatedDates, projectsSearch, tags, } = viewParams.sieve;
    const { currentPage, pageSize } = viewParams;
    const baseQuery = {
        currentPage,
        pageSize,
        orderon: String(sortBy),
        // NOTE: Antd uses ascend/descend, but backend uses ascending/descending
        orderby: sortOrder === 'ascend' ? 'ascending' : 'descending',
        createdFrom: serializeDate(createdDates === null || createdDates === void 0 ? void 0 : createdDates[0]),
        createdTo: serializeDate(createdDates === null || createdDates === void 0 ? void 0 : createdDates[1]),
        updatedFrom: serializeDate(updatedDates === null || updatedDates === void 0 ? void 0 : updatedDates[0]),
        updatedTo: serializeDate(updatedDates === null || updatedDates === void 0 ? void 0 : updatedDates[1]),
        tags: tags === undefined ? [] : tags,
    };
    if (projectsSearch === undefined) {
        return baseQuery;
    }
    return Object.assign(Object.assign({}, baseQuery), { searching: true, all: convertToElasticFormat(projectsSearch.all, projectsSearch.strict), projectname: convertToElasticFormat(projectsSearch.projectname, projectsSearch.strict), transcription: convertToElasticFormat(projectsSearch.transcription, projectsSearch.strict), speakers: convertToElasticFormat(projectsSearch.speakers, projectsSearch.strict), notes: convertToElasticFormat(projectsSearch.notes, projectsSearch.strict) });
};
const ProjectsPage = () => {
    var _a;
    const { session, updateUserSettings } = useSession();
    const { isSubdomain } = useAppSubdomain();
    const { notification } = App.useApp();
    const [liveUpdate] = useState(() => new LiveUpdateWebsocket('&usermessages=true'));
    const [projectsList, trackedProjects] = useTrackedProjects(liveUpdate, session.connection);
    const [transcribeModalVisible, setTranscribeModalVisible] = useState(false);
    const { tutorialProjectId, tutorialContinue, tutorialPrepareNext, tutorialStart, setTutorialProjectId, } = useBeeyTutorial();
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [showKeywords, setShowKeywords] = useState(session.login.hasClaim('keywords:enabled') && clientEnv.getShowKeywords());
    const [teamTags, setTeamTags] = useState('loading');
    const [currentAnnouncements, setCurrentAnnouncements] = useState([]);
    const [liveUpdateStatus, setLiveUpdateStatus] = useState({
        isDown: false,
        isGivenup: false,
        reviveAttemptCount: 1,
    });
    const { settings } = session.login.user;
    const params = new URLSearchParams(window.location.search);
    const tablePageSize = Number(params.get('pageSize'));
    const tableView = useTableView(PROJS_TABLE_ID, {
        pageSize: Number.isNaN(tablePageSize) || tablePageSize < 1
            ? 50
            : tablePageSize,
        sieve: {
            sortBy: 'created',
            filterDates: [],
            sortOrder: 'descend',
            createdDates: [],
            updatedDates: [],
            tags: [],
        },
    });
    const fetchTags = async () => {
        const tags = await fetchCurrentTeamTags(session.connection);
        setTeamTags(tags);
    };
    useEffect(() => {
        void fetchTags();
    }, []);
    useEffect(() => {
        const displayAnnouncement = async () => {
            const currentTime = dayjs().toISOString();
            const from = currentTime;
            const to = currentTime;
            const data = await Announcements.fetchAnnouncements(session.connection, from, to, {});
            if (data.list.length === 0) {
                return;
            }
            setCurrentAnnouncements(data.list);
        };
        void displayAnnouncement();
    }, []);
    useEffect(() => {
        if (projectsList === 'loading') {
            return;
        }
        if (tutorialProjectId !== 0) {
            tutorialContinue();
        }
        const { currentPage } = tableView.getViewParams();
        if (currentPage !== 1 && projectsList.items.length === 0) {
            tableView.setCurrentPage(currentPage - 1);
        }
        const maxPage = Math.max(1, Math.ceil(projectsList.totalCount / projectsList.query.pageSize));
        if (maxPage < currentPage) {
            tableView.setCurrentPage(maxPage);
        }
        if (projectsList.totalCount < selectedProjects.length) {
            setSelectedProjects(projectsList.items);
        }
        if (selectedProjects.length > 0) {
            const selectedProjectIds = selectedProjects.map((proj) => proj.id);
            const updatedSelectedProjects = projectsList.items.filter((proj) => selectedProjectIds.includes(proj.id));
            setSelectedProjects(updatedSelectedProjects.length > 0 ? updatedSelectedProjects : selectedProjects);
        }
        notification.destroy();
    }, [projectsList]);
    const fetchProjects = async (showLoadingAnimation) => {
        await trackedProjects.reload(constructProjectsQueryFromView(tableView.getViewParams()), showLoadingAnimation);
    };
    const handleSearch = (searchFormValues) => {
        if (searchFormValues !== undefined) {
            tableView.setCurrentPage(1);
        }
        const oldSieve = tableView.getViewParams().sieve;
        if (oldSieve === null) {
            throw new Error('sieve is null');
        }
        tableView.setSieve(Object.assign(Object.assign({}, oldSieve), { projectsSearch: searchFormValues }));
    };
    const handleTagRemoved = (tag) => {
        var _a, _b;
        const sieve = (_a = tableView.getViewParams().sieve) !== null && _a !== void 0 ? _a : { filterDates: [] };
        const { projectsSearch } = sieve;
        if (projectsSearch === undefined) {
            global.logger.error('cannot remove tag, because projectsSearch is undefined');
            return;
        }
        const newSearch = Object.assign(Object.assign({}, projectsSearch), { [tag]: '' });
        if (tag === 'inAnyField') {
            newSearch.all = '';
        }
        const { all, projectname, transcription, speakers, notes, inAnyField, } = newSearch;
        const oldSieve = (_b = tableView.getViewParams().sieve) !== null && _b !== void 0 ? _b : { filterDates: [] };
        if ([all, projectname, transcription, speakers, notes, inAnyField].every((item) => item === '')) {
            tableView.setSieve(Object.assign(Object.assign({}, oldSieve), { projectsSearch: undefined }));
            return;
        }
        tableView.setSieve(Object.assign(Object.assign({}, oldSieve), { projectsSearch: newSearch }));
    };
    const refreshProjects = (showLoadingAnimation) => {
        void fetchProjects(showLoadingAnimation);
    };
    useEffect(() => {
        liveUpdate.onReady = () => setLiveUpdateStatus({
            isDown: false,
            isGivenup: false,
            reviveAttemptCount: 1,
        });
        liveUpdate.onReviveAttempt = (attemptCount) => setLiveUpdateStatus(Object.assign(Object.assign({}, liveUpdateStatus), { reviveAttemptCount: attemptCount }));
        liveUpdate.onBroken = () => setLiveUpdateStatus(Object.assign(Object.assign({}, liveUpdateStatus), { isDown: true }));
        liveUpdate.onGiveup = () => setLiveUpdateStatus(Object.assign(Object.assign({}, liveUpdateStatus), { isGivenup: true }));
        return () => liveUpdate.close();
    }, []);
    const handleClearAll = () => {
        var _a;
        const oldSieve = (_a = tableView.getViewParams().sieve) !== null && _a !== void 0 ? _a : { filterDates: [] };
        tableView.setSieve(Object.assign(Object.assign({}, oldSieve), { sortBy: 'created', sortOrder: 'descend', tags: [], createdDates: [], updatedDates: [] }));
    };
    const showTranscribeModal = () => {
        setTranscribeModalVisible(true);
    };
    const closeTranscribeModal = () => {
        setTranscribeModalVisible(false);
    };
    const handleGlobalSelection = async (selected) => {
        if (selected === true) {
            const query = constructProjectsQueryFromView(Object.assign(Object.assign({}, tableView.getViewParams()), { currentPage: 1, pageSize: GLOBAL_SELECTION_LIMIT }));
            const allProjects = await ProjectAPI.fetchProjectsList(session.connection, query);
            setSelectedProjects(allProjects.items);
        }
        else {
            setSelectedProjects([]);
        }
    };
    const { sortBy, sortOrder, createdDates, updatedDates, projectsSearch, tags, } = ((_a = tableView.getViewParams().sieve) !== null && _a !== void 0 ? _a : { filterDates: [] });
    const uiLocale = getCurrentUiLocale().code;
    const localeAnnouncements = currentAnnouncements
        .filter((localVariant) => localVariant.localizations[uiLocale])
        .map((item) => {
        const markdownContent = renderMarkdown(item.localizations[uiLocale]);
        const content = (_jsx("div", { 
            // NOTE: dangerouslySetInnerHTML is used here because of the markdown rendering
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML: { __html: markdownContent } }));
        return ({
            locale: uiLocale,
            content,
            id: item.id,
        });
    });
    const announcementAlert = localeAnnouncements.map((item) => {
        return (_jsx(Alert, { className: styles.announcementAlert, message: item.content, banner: true, type: "info", closable: true, onClose: () => Announcements.dismissAnnouncement(session.connection, item.id) }, item.id));
    });
    const skipTutorial = () => {
        updateUserSettings({ startBeeyTutorial: false });
    };
    const createDemoProject = async () => {
        const result = await ProjectAPI.createDemoProject(session.connection);
        if (result.isSuccess()) {
            setTutorialProjectId(result.get());
        }
        else {
            notification.error({ message: txt('demoProjectCreationFailed') });
        }
    };
    const runTutorial = async () => {
        await createDemoProject();
        refreshProjects(false);
        tutorialStart();
        updateUserSettings({ startBeeyTutorial: false });
    };
    const handleExportProjectsView = async () => {
        await handleProjectsViewExport(session.connection, constructProjectsQueryFromView(tableView.getViewParams()));
    };
    const handleShowKeywords = () => {
        clientEnv.setShowKeywords(!clientEnv.getShowKeywords());
        setShowKeywords(!showKeywords);
    };
    return (_jsx(DashboardPage, { title: `${txt('projects')}`, toolbar: (_jsx(FulltextSearchBar, { onSearchChanged: handleSearch, searchParams: projectsSearch })), children: ({ contentHeight }) => (_jsxs(_Fragment, { children: [_jsx(LiveUpdateStatus, { socketStatus: liveUpdateStatus }), _jsxs(TutorialModal, { skipTutorial: skipTutorial, tutorialAction: runTutorial, skipTutorialLabel: txt('tutorialSkip'), tutorialActionLabel: txt('tutorialStart'), visible: settings.startBeeyTutorial && session.login.hasClaim('projects:transcribe'), children: [_jsx("img", { src: isSubdomain ? beeyLogoGrey : beeyLogo, className: tutorialModalStyles.image, alt: "Beey logo" }), _jsx("h2", { children: txt('beeyTutorialStartHeading') }), _jsx("p", { children: txt('beeyTutorialStartText') })] }), _jsx(TranscribeModal, { visible: transcribeModalVisible, onClose: closeTranscribeModal }), announcementAlert, (projectsSearch !== undefined) ? (_jsx("div", { className: styles.tagPanel, children: _jsx(FulltextSearchTags, { onTagRemoved: handleTagRemoved, onResetFulltextSearch: () => handleSearch(), searchParams: projectsSearch }) })) : null, _jsxs(Card, { className: styles.projectsTable, bordered: false, children: [_jsx(ProjectsToolbar, { selectedProjects: selectedProjects, sortBy: sortBy, sortOrder: sortOrder, tags: tags, onDeselect: () => handleGlobalSelection(false), projectsCount: projectsList === 'loading' ? null : projectsList.totalCount, createdDates: createdDates !== null && createdDates !== void 0 ? createdDates : [], updatedDates: updatedDates !== null && updatedDates !== void 0 ? updatedDates : [], onClearAll: handleClearAll, showTranscribeModal: showTranscribeModal, refreshProjects: refreshProjects, onExportProjectsView: handleExportProjectsView, onShowKeywords: handleShowKeywords, showKeywords: showKeywords, onRefetch: () => fetchProjects(false), onRefetchTags: () => fetchTags() }), _jsx(ProjectsTable, { contentHeight: contentHeight, tableView: tableView, selectedProjects: selectedProjects, refreshProjects: refreshProjects, sortBy: sortBy, sortOrder: sortOrder, createdDates: createdDates, updatedDates: updatedDates, tutorialProjectId: tutorialProjectId, tutorialPrepareNext: tutorialPrepareNext, tags: tags, projectsList: projectsList, setSelectedProjects: setSelectedProjects, fetchProjects: fetchProjects, showKeywords: showKeywords, teamTags: teamTags, fetchTags: fetchTags, onGlobalSelection: handleGlobalSelection, projectsSearch: projectsSearch })] })] })) }));
};
export default ProjectsPage;
